import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import {
  Brain, ChevronDown, ChevronUp, Loader2, RefreshCw,
  TrendingUp, TrendingDown, Sparkles, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  surface: "var(--bc-panel, #09090B)",
  highlight: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.07))",
  text: "var(--bc-ink, #FFFFFF)",
  muted: "var(--bc-sub, #71717A)",
  violet: "#A78BFA",
  cyan: "#06B6D4",
  green: "#22FFA3",
  amber: "#F59E0B",
  red: "#EF4444",
};

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v.toFixed ? v.toFixed(1) : v}%`);
const fmtRoi = (v) => {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};
const roiColor = (v) => {
  if (v === null || v === undefined) return C.muted;
  if (v > 5) return C.green;
  if (v > 0) return C.cyan;
  if (v > -5) return C.amber;
  return C.red;
};

function StatBlock({ label, stat }) {
  if (!stat || !stat.n) return null;
  return (
    <div style={{
      background: C.highlight,
      border: `1px solid ${C.border}`,
      padding: "0.55rem 0.7rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.25rem",
      minWidth: 110,
    }}>
      <div style={{
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: "0.6rem",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: C.muted,
      }}>{label}</div>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "baseline" }}>
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.95rem",
          fontWeight: 700,
          color: C.text,
        }}>{fmtPct(stat.win_rate)}</span>
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.7rem",
          color: roiColor(stat.roi_pct),
          fontWeight: 600,
        }}>
          ROI {fmtRoi(stat.roi_pct)}
        </span>
      </div>
      <div style={{
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: "0.62rem",
        color: C.muted,
      }}>n={stat.n} · {stat.wins}W/{stat.losses}L</div>
    </div>
  );
}

function MarketList({ rows, kind }) {
  if (!rows || rows.length === 0) return null;
  const isTop = kind === "top";
  const Icon = isTop ? TrendingUp : TrendingDown;
  const accent = isTop ? C.green : C.red;
  return (
    <div style={{ marginTop: "0.65rem" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.35rem",
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: "0.65rem",
        color: accent,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      }}>
        <Icon size={11} /> {isTop ? "Top Mercati" : "Mercati in perdita"}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
        {rows.slice(0, 5).map((r) => (
          <div key={r.market} style={{
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            gap: "0.6rem",
            padding: "0.3rem 0.5rem",
            background: C.highlight,
            border: `1px solid ${C.border}`,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.65rem",
            alignItems: "center",
          }}>
            <span style={{ color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.market}
            </span>
            <span style={{ color: C.muted }}>n={r.n} · {fmtPct(r.win_rate)}</span>
            <span style={{ color: roiColor(r.roi_pct), fontWeight: 700 }}>{fmtRoi(r.roi_pct)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AiMemoryWidget({ token, onToast }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(60);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // {digest, prompt_block, last_backfill_run}
  const [backfilling, setBackfilling] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/ai-picks/memory-stats`, {
        params: { days, admin_token: token },
      });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Errore caricamento memoria";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/admin/ai-picks/memory-stats`, {
          params: { days, admin_token: token },
        });
        if (!cancelled) {
          setData(res.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err?.response?.data?.detail || err.message || "Errore caricamento memoria";
          setError(msg);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, days, token]);

  const handleBackfill = async () => {
    if (backfilling || !token) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/ai-picks/backfill-outcomes`, {
        admin_token: token,
        max_age_days: days,
      });
      const s = res.data || {};
      onToast?.(
        `Memoria aggiornata: ${s.picks_scanned || 0} picks · ` +
        `${s.scored_win || 0}W ${s.scored_lose || 0}L ` +
        `(${s.still_pending || 0} pending)`,
      );
      await fetchStats();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Backfill fallito";
      onToast?.(msg, "error");
    } finally {
      setBackfilling(false);
    }
  };

  const digest = data?.digest;
  const lastRun = data?.last_backfill_run;

  return (
    <div
      data-testid="ai-memory-widget"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        marginBottom: "1rem",
      }}
    >
      {/* Header (toggle) */}
      <button
        type="button"
        data-testid="ai-memory-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          width: "100%",
          padding: "0.7rem 0.9rem",
          background: "transparent",
          border: "none",
          color: C.text,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Brain size={14} style={{ color: C.violet }} />
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif",
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontSize: "0.85rem",
        }}>
          Memoria Claude
        </span>
        <span style={{
          padding: "0.15rem 0.45rem",
          background: `${C.violet}1a`,
          border: `1px solid ${C.violet}55`,
          color: C.violet,
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.58rem",
          letterSpacing: "0.1em",
        }}>
          {digest?.samples != null ? `${digest.samples} samples` : "—"}
        </span>
        {digest?.overall?.roi_pct != null && (
          <span style={{
            padding: "0.15rem 0.45rem",
            background: `${roiColor(digest.overall.roi_pct)}1a`,
            border: `1px solid ${roiColor(digest.overall.roi_pct)}66`,
            color: roiColor(digest.overall.roi_pct),
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.6rem",
            fontWeight: 700,
          }}>
            ROI {fmtRoi(digest.overall.roi_pct)}
          </span>
        )}
        <span style={{ marginLeft: "auto", color: C.muted }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {/* Body (only when open) */}
      {open && (
        <div style={{ padding: "0 0.9rem 0.9rem 0.9rem", borderTop: `1px solid ${C.border}` }}>
          {/* Controls */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            flexWrap: "wrap",
            padding: "0.7rem 0",
          }}>
            <label style={{
              display: "flex", alignItems: "center", gap: "0.35rem",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.65rem", color: C.muted, letterSpacing: "0.08em",
            }}>
              FINESTRA:
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10))}
                data-testid="ai-memory-days-select"
                style={{
                  background: C.highlight,
                  border: `1px solid ${C.border}`,
                  color: C.text,
                  padding: "0.25rem 0.4rem",
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.7rem",
                  outline: "none",
                }}
              >
                <option value={14}>14 giorni</option>
                <option value={30}>30 giorni</option>
                <option value={60}>60 giorni</option>
                <option value={90}>90 giorni</option>
              </select>
            </label>
            <button
              onClick={fetchStats}
              disabled={loading}
              data-testid="ai-memory-refresh-btn"
              style={{
                display: "inline-flex", alignItems: "center", gap: "0.3rem",
                background: "transparent",
                border: `1px solid ${C.border}`,
                color: C.muted,
                padding: "0.35rem 0.65rem",
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.65rem", letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: loading ? "wait" : "pointer",
              }}
            >
              {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Ricarica
            </button>
            <button
              onClick={handleBackfill}
              disabled={backfilling}
              data-testid="ai-memory-backfill-btn"
              style={{
                display: "inline-flex", alignItems: "center", gap: "0.35rem",
                background: `${C.violet}1a`,
                border: `1px solid ${C.violet}88`,
                color: C.violet,
                padding: "0.4rem 0.75rem",
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 700,
                fontSize: "0.78rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                cursor: backfilling ? "wait" : "pointer",
                boxShadow: backfilling ? "none" : `0 0 10px ${C.violet}33`,
              }}
            >
              {backfilling ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {backfilling ? "In aggiornamento…" : "Aggiorna memoria"}
            </button>
            {lastRun?.finished_at && (
              <span style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.6rem",
                color: C.muted,
              }}>
                Ultimo run: {new Date(lastRun.finished_at).toLocaleString("it-IT")}
              </span>
            )}
          </div>

          {error && (
            <div style={{
              display: "flex", alignItems: "center", gap: "0.4rem",
              padding: "0.45rem 0.6rem",
              background: `${C.red}15`,
              border: `1px solid ${C.red}55`,
              color: C.red,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.7rem",
              marginBottom: "0.6rem",
            }}>
              <AlertCircle size={12} /> {error}
            </div>
          )}

          {/* Empty / insufficient sample state */}
          {!loading && digest && digest.samples < 10 && (
            <div style={{
              padding: "0.7rem 0.85rem",
              background: C.highlight,
              border: `1px dashed ${C.amber}55`,
              color: C.amber,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.72rem",
              lineHeight: 1.5,
            }}>
              ⏳ Campione insufficiente — {digest.samples} picks chiuse negli ultimi {days} giorni.
              Sotto i 10 sample la memoria NON condiziona Claude (evita overfitting).
              Lancia <strong>&quot;Aggiorna memoria&quot;</strong> dopo che le partite di oggi sono terminate,
              oppure attendi che il cron notturno (23:30) lo faccia automaticamente.
            </div>
          )}

          {/* Stats */}
          {digest && digest.samples >= 10 && (
            <>
              {/* Overall */}
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.6rem" }}>
                <StatBlock label="Overall" stat={digest.overall} />
              </div>

              {/* By confidence */}
              {digest.by_confidence && Object.keys(digest.by_confidence).length > 0 && (
                <div style={{ marginBottom: "0.65rem" }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.62rem",
                    color: C.muted,
                    letterSpacing: "0.1em",
                    marginBottom: "0.35rem",
                    textTransform: "uppercase",
                  }}>Per Confidence</div>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    <StatBlock label="ALTA" stat={digest.by_confidence.ALTA} />
                    <StatBlock label="MEDIA" stat={digest.by_confidence.MEDIA} />
                    <StatBlock label="BASSA" stat={digest.by_confidence.BASSA} />
                  </div>
                </div>
              )}

              {/* By type */}
              {digest.by_type && Object.keys(digest.by_type).length > 0 && (
                <div style={{ marginBottom: "0.65rem" }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.62rem",
                    color: C.muted,
                    letterSpacing: "0.1em",
                    marginBottom: "0.35rem",
                    textTransform: "uppercase",
                  }}>Singolo vs Combo</div>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    <StatBlock label="Singolo" stat={digest.by_type.single} />
                    <StatBlock label="Combo" stat={digest.by_type.combo} />
                  </div>
                </div>
              )}

              {/* By combo family */}
              {digest.by_combo_family && Object.keys(digest.by_combo_family).length > 0 && (
                <div style={{ marginBottom: "0.65rem" }}>
                  <div style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.62rem",
                    color: C.muted,
                    letterSpacing: "0.1em",
                    marginBottom: "0.35rem",
                    textTransform: "uppercase",
                  }}>Per Famiglia Combo</div>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    <StatBlock label="A · DC+O/U" stat={digest.by_combo_family.A} />
                    <StatBlock label="B · MG Casa+Tr" stat={digest.by_combo_family.B} />
                    <StatBlock label="C · HT+ST" stat={digest.by_combo_family.C} />
                  </div>
                </div>
              )}

              {/* Top / Worst markets */}
              <MarketList rows={digest.top_markets} kind="top" />
              <MarketList rows={digest.worst_markets} kind="worst" />
            </>
          )}
        </div>
      )}
    </div>
  );
}
